/**
 * Jadro schedule engine, vrstva 4: spec + okno → výskyty (anchored stride enumerácia).
 * Celá matematika v civilnom priestore, instant sa počíta AŽ na konci raz. Ohraničené alebo HODÍ.
 * Poradie riešenia (§3): monthdayOverflow → businessDayAdjust → end → time → civilToInstant.
 * Čistý, deterministický, zero I/O.
 */

import { civilToInstant, addCivilDays, isoWeekday, lastDayOfMonth, compareCivil } from './civil.js';
import { adjustToWorkingDay } from './holidays.js';
import { normalizeSpec } from './spec.js';

const MAX_OCCURRENCES = 1000;
const WEEKDAYS = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
const SAFETY_PERIODS = 5_000_000;
const isDateStr = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

function parts(date) {
  return { y: +date.slice(0, 4), m: +date.slice(5, 7), d: +date.slice(8, 10) };
}
function ymd(y, m, d) {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function daysInMonth(y, m) {
  return +lastDayOfMonth(y, m).slice(8, 10);
}

// Vyrieš jeden byMonthday záznam v (y,m) s overflow → dátum alebo null (skip).
function resolveMonthday(y, m, md, overflow) {
  if (md === -1) return lastDayOfMonth(y, m);
  const dim = daysInMonth(y, m);
  if (md <= dim) return ymd(y, m, md);
  return overflow === 'skip' ? null : lastDayOfMonth(y, m); // clamp
}

function dedupeSorted(dates) {
  return [...new Set(dates)].sort(compareCivil);
}

// Iterátor základných (pred-adjust) dátumov v vzostupnom poradí, od anchoru.
function baseDateIterator(s) {
  const anchor = s.anchor.date;
  const { y: ay, m: am, d: ad } = parts(anchor);
  let buffer = [];
  let period = 0;

  function refill() {
    while (buffer.length === 0 && period < SAFETY_PERIODS) {
      if (s.freq === 'daily') {
        buffer.push(addCivilDays(anchor, period * s.interval));
      } else if (s.freq === 'weekly') {
        const anchorMonday = addCivilDays(anchor, -(isoWeekday(anchor) - 1));
        const weekStart = addCivilDays(anchorMonday, period * s.interval * 7);
        const cands = s.byWeekday
          .map((wd) => addCivilDays(weekStart, WEEKDAYS.indexOf(wd)))
          .filter((d) => compareCivil(d, anchor) >= 0);
        buffer.push(...dedupeSorted(cands));
      } else if (s.freq === 'monthly') {
        const base = ay * 12 + (am - 1) + period * s.interval;
        const y = Math.floor(base / 12);
        const m = (base % 12) + 1;
        const mds = s.byMonthday && s.byMonthday.length ? s.byMonthday : [ad];
        const cands = mds
          .map((md) => resolveMonthday(y, m, md, s.monthdayOverflow))
          .filter(Boolean)
          .filter((d) => compareCivil(d, anchor) >= 0);
        buffer.push(...dedupeSorted(cands));
      } else if (s.freq === 'yearly') {
        const y = ay + period * s.interval;
        const mds = s.byMonthday && s.byMonthday.length ? s.byMonthday : [ad];
        const cands = mds
          .map((md) => resolveMonthday(y, am, md, s.monthdayOverflow))
          .filter(Boolean)
          .filter((d) => compareCivil(d, anchor) >= 0);
        buffer.push(...dedupeSorted(cands));
      }
      period += 1;
    }
  }

  return {
    next() {
      refill();
      return buffer.length ? buffer.shift() : null;
    },
  };
}

// Vyrieš základný dátum → výskyt: businessDayAdjust → time → instant.
function resolveOccurrence(s, baseDate, seq, calendar) {
  const mode = s.businessDayAdjust || 'none';
  const date = adjustToWorkingDay(baseDate, mode, calendar);
  const time = s.anchor.time ?? null;
  return { date, time, dueAtInstant: civilToInstant(date, time), seq };
}

// Vygeneruj výskyty v poradí, so stop predikátom. seq počíta od anchoru (emitované).
function* occurrenceStream(s, calendar) {
  if (s.kind === 'one_off') {
    yield resolveOccurrence(s, s.anchor.date, 0, calendar);
    return;
  }
  const it = baseDateIterator(s);
  const seen = new Set();
  let seq = 0;
  let base = it.next();
  while (base !== null) {
    // end: count — zastav po n emitovaných; until — zastav keď adjusted > until
    if (s.end && s.end.type === 'count' && seq >= s.end.n) return;
    const occ = resolveOccurrence(s, base, seq, calendar);
    const key = `${occ.date}|${occ.time}`;
    if (!seen.has(key)) {
      if (s.end && s.end.type === 'until' && compareCivil(occ.date, s.end.date) > 0) {
        // za hranicou until — ale ďalšie base dátumy môžu byť ešte pred until? nie, vzostupné.
        return;
      }
      seen.add(key);
      yield occ;
      seq += 1;
    }
    base = it.next();
  }
}

/**
 * Jadro. spec + okno {from,to} | {limit,asOf} + kalendár → Occurrence[].
 * Ohraničené alebo hodí E_UNBOUNDED / E_WINDOW_TOO_LARGE. Vzostupné, deduplikované.
 */
export function expandOccurrences(spec, query, calendar) {
  const s = normalizeSpec(spec);
  const q = query || {};
  const hasWindow = isDateStr(q.from) && isDateStr(q.to);
  const hasLimit = Number.isInteger(q.limit) && Number.isInteger(q.asOf);

  if (!hasWindow && !hasLimit) {
    // Bez okna aj bez limitu je ohraničené len ak end nie je "never".
    if (s.kind === 'recurring' && (!s.end || s.end.type === 'never')) {
      throw new Error('E_UNBOUNDED: expandOccurrences requires {from,to} or {limit,asOf}');
    }
  }

  const results = [];
  for (const occ of occurrenceStream(s, calendar)) {
    if (hasWindow) {
      if (compareCivil(occ.date, q.from) < 0) continue;
      if (compareCivil(occ.date, q.to) > 0) break; // vzostupné → za oknom
      results.push(occ);
      if (results.length > MAX_OCCURRENCES) {
        throw new Error('E_WINDOW_TOO_LARGE: window would yield more than 1000 occurrences');
      }
    } else if (hasLimit) {
      if (occ.dueAtInstant <= q.asOf) continue;
      results.push(occ);
      if (results.length >= q.limit) break;
    } else {
      results.push(occ);
      if (results.length > MAX_OCCURRENCES) {
        throw new Error('E_WINDOW_TOO_LARGE: unbounded-by-count exceeded 1000 occurrences');
      }
    }
  }
  return results;
}

/**
 * Prvý výskyt s dueAtInstant > asOf; null keď je rozvrh vyčerpaný.
 */
export function nextOccurrence(spec, asOf, calendar) {
  const [occ] = expandOccurrences(spec, { limit: 1, asOf }, calendar);
  return occ || null;
}
